import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";
import { useSocketContext } from "src/context/SocketContext";
import { useSelector } from "react-redux";
import authConfig from "src/configs/auth";
import { toast } from "react-hot-toast";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import moment from "moment";
import { useUpload } from "src/contexts/UploadContext";

/**
 * NotesActionSheet Component
 * 
 * Displays a movable and resizable dialog for viewing and responding to notes (mini conversations).
 * Shows messages from the mini conversation and allows users to send replies.
 * Supports multiple instances - users can open multiple mini chat dialogs simultaneously.
 */

// Styled Components - Following OrderDetailsDialog pattern
const CustomOverlay = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10000,
  pointerEvents: "none",
});

const CustomDialog = styled(Box)(({ theme }) => ({
  borderRadius: 20,
  maxWidth: "95vw",
  maxHeight: "95vh",
  width: "500px",
  height: "600px",
  position: "fixed",
  overflow: "hidden",
  background: "white",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  pointerEvents: "auto",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: { width: "90vw", height: "80vh" },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
    height: "80vh",
    borderRadius: 16,
  },
  [theme.breakpoints.down(400)]: {
    width: "90vw",
    height: "80vh",
    borderRadius: 12,
  },
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 25,
  height: 25,
  cursor: "se-resize",
  background:
    "linear-gradient(135deg, rgba(224,224,224,0.9) 0%, rgba(189,189,189,0.9) 100%)",
  borderTopLeftRadius: 20,
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 15,
    height: 15,
    background: `repeating-linear-gradient(
      45deg, 
      rgba(158,158,158,0.8), 
      rgba(158,158,158,0.8) 2px, 
      transparent 2px, 
      transparent 4px
    )`,
  },
  [theme.breakpoints.down("sm")]: {
    width: 20,
    height: 20,
    borderTopLeftRadius: 16,
    "&::before": {
      bottom: 1,
      right: 1,
      width: 12,
      height: 12,
    },
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 24px",
  background: "white",
  position: "relative",
  zIndex: 10,
  cursor: "move",
  userSelect: "none",
  borderBottom: "1px solid #e5e7eb",
  [theme.breakpoints.down("sm")]: { padding: "12px 16px" },
  [theme.breakpoints.down(400)]: { padding: "10px 12px" },
}));

const DragHandle = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "50%",
  top: 8,
  transform: "translateX(-50%)",
  width: 40,
  height: 4,
  backgroundColor: "#E2E8F0",
  borderRadius: 2,
  cursor: "move",
  "&:hover": { backgroundColor: "#CBD5E1" },
  [theme.breakpoints.down("sm")]: { width: 32, height: 3, top: 6 },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  background: "white",
  borderRadius: 16,
  height: "calc(100% - 100px)",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "0",
  [theme.breakpoints.down("sm")]: {
    height: "calc(100% - 80px)",
    borderRadius: 12,
  },
  [theme.breakpoints.down(400)]: {
    height: "calc(100% - 70px)",
    borderRadius: 8,
  },
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(1),
  backgroundColor: "#f3f4f6",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#c1c1c1",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#a8a8a8",
  },
}));

const NotesActionSheet = ({
  open,
  onClose,
  miniConversationId,
  message,
}) => {
  const theme = useTheme();
  const { emit, addEventListener, removeEventListener, sendMessageOnMiniConversation, getMessagesMiniConversation } = useSocketContext();
  const messagesContainerRef = useRef(null);
  const dialogRef = useRef(null);
  const hasFetchedMessagesRef = useRef(false);
  const loadingTimeoutRef = useRef(null);
  
  // Get current user ID
  const currentUser = useSelector((state) => state.auth.user);
  const storedUserId =
    typeof window !== "undefined"
      ? window.localStorage.getItem(authConfig.storageUserIdKeyName)
      : null;
  const currentUserId = currentUser?.userId ?? storedUserId;

  // Upload context for background uploads
  const { uploadFiles } = useUpload();

  // State for messages
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [messageMentions, setMessageMentions] = useState([]);
  const [isConnected, setIsConnected] = useState(true);
  
  // State for mini conversation info
  const [miniConversationInfo, setMiniConversationInfo] = useState(null);

  // Reply preview state - tracks which message is being replied to
  const [replyPreview, setReplyPreview] = useState({
    isActive: false,
    message: null,
  });

  // Separate state for media/files in notes action sheet
  const [notesMediaState, setNotesMediaState] = useState({
    selectedFiles: [],
    isUploading: false,
    uploadProgress: {},
  });

  // Dialog positioning and resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ x: 100, y: 100 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Fetch messages when action sheet opens - only fetch once per miniConversationId
  useEffect(() => {
    if (open && miniConversationId) {
      // Only fetch messages if we haven't fetched them yet for this miniConversationId
      if (!hasFetchedMessagesRef.current) {
        fetchMessages();
        hasFetchedMessagesRef.current = true;
      }
      // Fetch mini conversation info
      fetchMiniConversationInfo();
      joinConversation();
      setupMessageListener();
      setupMiniConversationInfoListener();
      // Mark messages as read when mini chat is opened
      markMessagesAsRead();
    } else {
      // Only clear input state when action sheet closes, preserve messages
      setNewMessage("");
      setMessageMentions([]);
      setNotesMediaState({
        selectedFiles: [],
        isUploading: false,
        uploadProgress: {},
      });
      // Clear mini conversation info when dialog closes
      setMiniConversationInfo(null);
      // Clear reply preview when dialog closes
      setReplyPreview({
        isActive: false,
        message: null,
      });
    }

    return () => {
      // Only cleanup when component unmounts or miniConversationId changes
      if (miniConversationId) {
        leaveConversation();
        cleanupMessageListener();
        cleanupMiniConversationInfoListener();
      }
      // Clear loading timeout on cleanup
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [open, miniConversationId]);

  // Reset fetch flag when miniConversationId changes (new dialog instance)
  useEffect(() => {
    hasFetchedMessagesRef.current = false;
  }, [miniConversationId]);

  // Auto-scroll to bottom when new messages arrive and mark as read
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
    // Mark messages as read when new messages arrive and dialog is open
    if (open && messages.length > 0) {
      markMessagesAsRead();
    }
  }, [messages, open]);

  // Initialize dialog position when opened
  useEffect(() => {
    if (open) {
      // Calculate position to avoid overlapping with other dialogs
      // Use a staggered position based on miniConversationId hash
      const hash = miniConversationId ? miniConversationId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
      const offsetX = (hash % 5) * 50; // Stagger horizontally
      const offsetY = (hash % 3) * 50; // Stagger vertically
      
      const isMobile = window.innerWidth < 960;
      const dialogWidth = isMobile ? window.innerWidth * 0.9 : 500;
      const dialogHeight = isMobile ? window.innerHeight * 0.8 : 600;
      const centerX = (window.innerWidth - dialogWidth) / 2 + offsetX;
      const centerY = (window.innerHeight - dialogHeight) / 2 + offsetY;

      setDialogPosition({
        x: Math.max(0, Math.min(centerX, window.innerWidth - dialogWidth)),
        y: Math.max(0, Math.min(centerY, window.innerHeight - dialogHeight)),
      });
    }
  }, [open, miniConversationId]);

  // Fix z-index for popups, menus, and tooltips to appear above dialog
  useEffect(() => {
    if (!open || !dialogRef.current) return;

    const dialogElement = dialogRef.current;

    const fixPopupZIndex = () => {
      // Find all Popover, Menu, Tooltip elements that are popups
      const popups = document.querySelectorAll(
        '.MuiPopover-root, .MuiMenu-root, .MuiTooltip-popper, [role="presentation"]'
      );
      
      popups.forEach((popup) => {
        if (popup instanceof HTMLElement) {
          const computedStyle = window.getComputedStyle(popup);
          // Only update if it's a popup (has position fixed or absolute)
          if (computedStyle.position === 'fixed' || computedStyle.position === 'absolute') {
            // Check if this popup is inside our dialog
            if (dialogElement.contains(popup)) {
              // This popup is inside our dialog, set high z-index
              popup.style.zIndex = '20000';
              
              // Also update any child Paper elements
              const paper = popup.querySelector('.MuiPaper-root');
              if (paper instanceof HTMLElement) {
                paper.style.zIndex = '20000';
              }
            } else {
              // Popup is likely in a portal, check if its anchor is in our dialog
              // MUI Popover stores anchor element reference
              const anchorId = popup.getAttribute('aria-describedby') || popup.getAttribute('aria-labelledby');
              if (anchorId) {
                const anchorElement = document.getElementById(anchorId);
                if (anchorElement && dialogElement.contains(anchorElement)) {
                  // Anchor is in our dialog, this popup belongs to our dialog
                  popup.style.zIndex = '20000';
                  const paper = popup.querySelector('.MuiPaper-root');
                  if (paper instanceof HTMLElement) {
                    paper.style.zIndex = '20000';
                  }
                }
              }
              
              // Also check if popup overlaps with dialog (fallback method)
              const popupRect = popup.getBoundingClientRect();
              const dialogRect = dialogElement.getBoundingClientRect();
              
              // If popup overlaps with dialog area and has low z-index, it's likely related
              const overlaps = !(
                popupRect.right < dialogRect.left ||
                popupRect.left > dialogRect.right ||
                popupRect.bottom < dialogRect.top ||
                popupRect.top > dialogRect.bottom
              );
              
              const currentZIndex = parseInt(computedStyle.zIndex) || 0;
              if (overlaps && currentZIndex < 20000) {
                popup.style.zIndex = '20000';
                const paper = popup.querySelector('.MuiPaper-root');
                if (paper instanceof HTMLElement) {
                  paper.style.zIndex = '20000';
                }
              }
            }
          }
        }
      });
    };

    // Run immediately
    fixPopupZIndex();
    
    // Set up interval to check periodically (for when menu/popup opens)
    const interval = setInterval(fixPopupZIndex, 100);
    
    // Also set up a mutation observer to catch dynamically added popups
    const observer = new MutationObserver(() => {
      setTimeout(fixPopupZIndex, 0);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [open, miniConversationId]);

  // Drag and resize functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - 400;
        const maxY = window.innerHeight - 300;
        setDialogPosition({
          x: Math.max(0, Math.min(e.clientX - dragOffset.x, maxX)),
          y: Math.max(0, Math.min(e.clientY - dragOffset.y, maxY)),
        });
      } else if (isResizing && dialogRef.current) {
        const rect = dialogRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;

        if (newWidth > 400 && newWidth < window.innerWidth * 0.95) {
          dialogRef.current.style.width = `${newWidth}px`;
        }
        if (newHeight > 300 && newHeight < window.innerHeight * 0.95) {
          dialogRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset]);

  // Event handlers for drag and resize
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  // Fetch messages for the mini conversation
  const fetchMessages = async () => {
    if (!miniConversationId) return;

    setIsLoading(true);
    
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Set a timeout to stop loading after 5 seconds if no response is received
    loadingTimeoutRef.current = setTimeout(() => {
      console.log("Loading timeout - no messages received, stopping loader");
      setIsLoading(false);
    }, 5000);
    
    try {
      // Emit getMessagesMiniConversation event to fetch messages for this mini conversation
      const success = getMessagesMiniConversation(miniConversationId);
      if (!success) {
        toast.error("failed to fetch messages - socket not connected");
        setIsLoading(false);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      }
      // Note: Messages will be received via messagesFetchedMiniConversation event listener
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("failed to load messages");
      setIsLoading(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }
  };

  // Fetch mini conversation info
  const fetchMiniConversationInfo = () => {
    if (!miniConversationId || !isConnected) return;
    
    try {
      // Emit getMiniConversationInfo event to fetch info for this mini conversation
      const success = emit("getMiniConversationInfo", { miniConversationId });
      if (!success) {
        console.error("failed to fetch mini conversation info - socket not connected");
      }
      // Note: Info will be received via miniConversationInfo event listener
    } catch (error) {
      console.error("Error fetching mini conversation info:", error);
    }
  };

  // Join the mini conversation
  const joinConversation = () => {
    if (!miniConversationId) return;
    emit("joinConversation", { conversationId: miniConversationId });
  };

  // Leave the mini conversation
  const leaveConversation = () => {
    if (!miniConversationId) return;
    emit("leaveConversation", { conversationId: miniConversationId });
  };

  // Mark messages as read - emit read receipt for mini conversation
  const markMessagesAsRead = () => {
    if (!miniConversationId || !currentUserId) return;
    
    // Find all unread messages (messages not sent by current user)
    const unreadMessages = messages.filter((msg) => {
      const senderIdStr = String(msg.senderId?._id || msg.senderId || "");
      const currentUserIdStr = String(currentUserId || "");
      const isNotOwn = senderIdStr !== currentUserIdStr;
      
      // Check if message is already read by current user
      const isAlreadyRead = msg.readBy?.some((readUser) => {
        const readUserIdStr = String(readUser.userId?._id || readUser.userId || readUser._id || readUser || "");
        return readUserIdStr === currentUserIdStr;
      });
      
      return isNotOwn && !isAlreadyRead;
    });
    
    // If there are unread messages, emit read receipt for each
    if (unreadMessages.length > 0) {
      console.log(`Marking ${unreadMessages.length} messages as read in mini conversation:`, miniConversationId);
      
      unreadMessages.forEach((msg) => {
        const messageId = msg._id || msg.id;
        if (messageId) {
          emit("markAsRead", {
            messageId: messageId,
            conversationId: miniConversationId,
          });
        }
      });
    }
  };

  // Setup message listener for receiving new messages
  const setupMessageListener = () => {
    if (!miniConversationId) return;

    const handleMessageReceived = (data) => {
      // Handle different message data structures
      let message = null;
      let conversationId = null;

      // Check if data has a message property (batch format)
      if (data.batch && Array.isArray(data.batch)) {
        // Handle batch messages
        for (const batchItem of data.batch) {
          if (batchItem.message && batchItem.conversationId === miniConversationId) {
            message = batchItem.message;
            conversationId = batchItem.conversationId;
            break;
          }
        }
      } else if (data.message) {
        // Single message format
        message = data.message;
        conversationId = data.conversationId || data.message?.conversationId;
      } else if (data.conversationId === miniConversationId) {
        // Direct message format
        message = data;
        conversationId = data.conversationId;
      }

      // Only process messages for this mini conversation
      if (message && conversationId === miniConversationId) {
        setMessages((prev) => {
          // Check if message already exists
          const messageId = message._id || message.id;
          const existingIndex = prev.findIndex(
            (msg) => (msg._id || msg.id) === messageId
          );

          // Mark isOwn property and preserve reactions
          // Add reply information - handle both ID and full object formats
          const processedMessage = {
            ...message,
            id: message._id || message.id,
            isOwn: (message.senderId?._id || message.senderId) === currentUserId,
            text: message.message || message.text || "",
            timestamp: message.createdAt
              ? moment(message.createdAt).format("HH:mm")
              : "",
            attachments: Array.isArray(message.attachments) ? message.attachments : [],
            reactions: message.reactions || [], // Preserve reactions from backend
            // Add reply information - handle both ID and full object formats
            replyTo: message.replyTo?._id || message.replyTo || null,
            replyToText:
              message.replyTo?.message || message.replyToText || null,
            replyToSender:
              message.replyTo?.senderId?.fullName ||
              message.replyTo?.senderId?.userName ||
              message.replyToSender ||
              null,
            // Include attachments from the original replied message if available
            replyToAttachments:
              message.replyTo?.attachments ||
              message.replyToAttachments ||
              [],
          };

          // If message exists, update it (for reaction updates, edits, etc.)
          if (existingIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[existingIndex] = processedMessage;
            return updatedMessages;
          }

          // Add new message
          return [...prev, processedMessage];
        });
      }
    };

    const handleMessagesReceived = (data) => {
      // Handle initial messages load from regular getMessages (fallback)
      // CRITICAL: Only process if this data is for THIS specific dialog
      const dataConversationId = data.conversationId || data.miniConversationId?._id || data.miniConversationId;
      if (String(dataConversationId) === String(miniConversationId) && data.messages) {
        // Process messages similar to ChatView - mark isOwn property and preserve reactions
        const processedMessages = data.messages.map((msg) => ({
          ...msg,
          id: msg._id || msg.id,
          isOwn: (msg.senderId?._id || msg.senderId) === currentUserId,
          text: msg.message || msg.text || "",
          timestamp: msg.createdAt
            ? moment(msg.createdAt).format("HH:mm")
            : "",
          attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
          reactions: msg.reactions || [], // Preserve reactions from backend
          // Add reply information - handle both ID and full object formats
          replyTo: msg.replyTo?._id || msg.replyTo || null,
          replyToText:
            msg.replyTo?.message || msg.replyToText || null,
          replyToSender:
            msg.replyTo?.senderId?.fullName ||
            msg.replyTo?.senderId?.userName ||
            msg.replyToSender ||
            null,
          // Include attachments from the original replied message if available
          replyToAttachments:
            msg.replyTo?.attachments ||
            msg.replyToAttachments ||
            [],
        }));
        setMessages(processedMessages);
        setIsLoading(false);
      }
    };

    // Listener for messagesFetchedMiniConversation - handles initial messages load
    const handleMessagesFetchedMiniConversation = (data) => {
      console.log("handleMessagesFetchedMiniConversation - received data:", data);
      console.log("Current miniConversationId:", miniConversationId);

      // Validate data
      if (!data) {
        console.log("Invalid data");
        return;
      }

      // CRITICAL: First check if this data is for THIS specific dialog instance
      // Check if data has a conversationId or miniConversationId that matches
      const dataConversationId = data.conversationId || data.miniConversationId?._id || data.miniConversationId;
      
      // If data has a conversationId and it doesn't match, ignore it completely
      if (dataConversationId && String(dataConversationId) !== String(miniConversationId)) {
        console.log("Data is for different conversation, ignoring:", {
          dataConversationId,
          currentMiniConvId: miniConversationId
        });
        return; // Don't process data for other dialogs - this prevents clearing messages
      }

      if (data.messages && Array.isArray(data.messages)) {
        console.log("Processing messages:", data.messages.length);
        
        // First, check if ANY message in the data belongs to this dialog
        // This is an additional safety check in case data doesn't have top-level conversationId
        const hasMatchingMessage = data.messages.some((msg) => {
          const msgMiniConvId = 
            msg.miniConversationId?._id || 
            msg.miniConversationId || 
            msg.conversationId;
          return String(msgMiniConvId) === String(miniConversationId) && 
                 msg.conversationType === "miniConversation";
        });
        
        // If no matching messages and no matching conversationId, ignore this data
        // This prevents clearing messages when data is for a different dialog
        if (!hasMatchingMessage && !dataConversationId) {
          console.log("No matching messages found and no conversationId match, ignoring data to preserve existing messages");
          // Still stop loading after timeout if no valid response
          return;
        }
        
        // If we have an empty messages array but conversationId matches, it's a valid empty response
        // This handles the case where there are no messages in the conversation
        if (data.messages.length === 0 && dataConversationId && String(dataConversationId) === String(miniConversationId)) {
          console.log("Empty messages array received for this conversation - no messages yet");
          setMessages([]);
          setIsLoading(false);
          // Clear loading timeout since we received a response
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          return;
        }
        
        // Filter and transform messages
        // Only include messages that belong to this mini conversation
        // Filter out add-remove type messages (those are notifications, not actual messages)
        const filteredMessages = data.messages.filter((msg) => {
          // Extract miniConversationId from message
          const msgMiniConvId = 
            msg.miniConversationId?._id || 
            msg.miniConversationId || 
            msg.conversationId;
          
          console.log("Checking message:", {
            msgId: msg._id,
            msgMiniConvId: msgMiniConvId,
            currentMiniConvId: miniConversationId,
            conversationType: msg.conversationType,
            type: msg.type,
          });
          
          // Check if message belongs to this mini conversation
          const belongsToMiniConv = String(msgMiniConvId) === String(miniConversationId);
          
          // Exclude add-remove type messages (these are notifications)
          const isNotAddRemove = msg.type !== "add-remove";
          
          // Only include messages with conversationType "miniConversation"
          const isValidType = msg.conversationType === "miniConversation";
          
          const shouldInclude = belongsToMiniConv && isNotAddRemove && isValidType;
          console.log("Should include message:", shouldInclude, {
            belongsToMiniConv,
            isNotAddRemove,
            isValidType,
          });
          
          return shouldInclude;
        });

        console.log("Filtered messages count:", filteredMessages.length);
        
        // Only update messages if:
        // 1. We have matching messages, OR
        // 2. The data conversationId matches (even if no messages, it's a valid empty response)
        const shouldUpdateMessages = filteredMessages.length > 0 || 
                                     (dataConversationId && String(dataConversationId) === String(miniConversationId));
        
        if (shouldUpdateMessages) {
          // Transform the incoming message format to match our local message structure
          const transformedMessages = filteredMessages.map((msg) => {
            const attachments = Array.isArray(msg.attachments)
              ? msg.attachments
              : [];
            
            // Ensure both values are strings for comparison to avoid type mismatch issues
            const senderIdStr = String(msg.senderId?._id || msg.senderId || "");
            const currentUserIdStr = String(currentUserId || "");
            const isOwnMessage = senderIdStr === currentUserIdStr;

            console.log("Transforming message:", {
              msgId: msg._id,
              senderId: senderIdStr,
              currentUserId: currentUserIdStr,
              isOwn: isOwnMessage,
              senderName: msg.senderId?.fullName || msg.senderId?.userName,
            });

            return {
              ...msg,
              id: msg._id || msg.id,
              isOwn: isOwnMessage,
              attachments: attachments,
              text: msg.message || msg.text || "",
              timestamp: msg.createdAt
                ? moment(msg.createdAt).format("HH:mm")
                : "",
              reactions: msg.reactions || [], // Preserve reactions from backend
              // Add reply information - handle both ID and full object formats
              replyTo: msg.replyTo?._id || msg.replyTo || null,
              replyToText:
                msg.replyTo?.message || msg.replyToText || null,
              replyToSender:
                msg.replyTo?.senderId?.fullName ||
                msg.replyTo?.senderId?.userName ||
                msg.replyToSender ||
                null,
              // Include attachments from the original replied message if available
              replyToAttachments:
                msg.replyTo?.attachments ||
                msg.replyToAttachments ||
                [],
            };
          });

          console.log("Transformed messages:", transformedMessages);
          setMessages(transformedMessages);
          setIsLoading(false);
          // Clear loading timeout since we received a response
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          console.log("Messages loaded  :", transformedMessages.length);
        } else {
          // Don't update messages if we're not sure this data is for our dialog
          console.log("Not updating messages - data doesn't clearly belong to this dialog, preserving existing messages");
          // Still stop loading after timeout if no valid response
        }
      } else {
        // Only clear messages if we're absolutely sure this data is for our dialog
        if (dataConversationId && String(dataConversationId) === String(miniConversationId)) {
          console.log("No messages in data but conversationId matches, clearing for this dialog");
          setMessages([]);
          setIsLoading(false);
          // Clear loading timeout since we received a response
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
        } else {
          console.log("No messages in data and data doesn't match this dialog, preserving existing messages");
          // Still stop loading after timeout if no valid response
        }
      }
    };

    // Listener for messageEdited event - handles reaction updates and message edits
    const handleMessageEdited = (data) => {
      console.log("handleMessageEdited - received data:", data);
      if (!data || !data.message) return;
     

      const editedMessage = data.message;
      
      // Extract miniConversationId from the edited message
      const messageMiniConvId = 
        editedMessage.miniConversationId?._id || 
        editedMessage.miniConversationId || 
        editedMessage.conversationId;
      
      // Only process if this message belongs to the current mini conversation
      if (String(messageMiniConvId) !== String(miniConversationId)) {
        return;
      }

      // Check if this is a mini conversation message
      if (editedMessage.conversationType !== "miniConversation") {
        return;
      }

      // Find and update the message in state
      setMessages((prev) => {
        const messageIndex = prev.findIndex((msg) => {
          return (msg._id === editedMessage._id || msg.id === editedMessage._id);
        });

        if (messageIndex !== -1) {
          const oldMessage = prev[messageIndex];
          
          // Check if this is actually a text edit or just a reaction update
          const isTextEdit = oldMessage.text !== (editedMessage.message || editedMessage.text);
          
          // Create updated message object with enhanced data from server
          const updatedMessage = {
            ...oldMessage, // Preserve all existing properties
            // Update core message content only if it's actually a text edit
            text: isTextEdit
              ? (editedMessage.message || editedMessage.text || oldMessage.text)
              : oldMessage.text,
            type: editedMessage.type || oldMessage.type,
            // Only set isEdited to true if it's actually a text edit, not a reaction update
            isEdited: isTextEdit ? true : oldMessage.isEdited,
            editedAt: isTextEdit
              ? (editedMessage.updatedAt || new Date().toISOString())
              : oldMessage.editedAt,
            editedBy: isTextEdit
              ? (editedMessage.editedBy || [])
              : oldMessage.editedBy,
            // Update server-generated properties
            _id: editedMessage._id,
            id: editedMessage._id,
            // Update attachments if provided
            attachments: editedMessage.attachments || oldMessage.attachments,
            // Update location data if provided
            location: editedMessage.location || oldMessage.location,
            latLong: editedMessage.latLong || oldMessage.latLong,
            // Update sender information if provided
            senderId: editedMessage.senderId || oldMessage.senderId,
            // Update reactions from server (this is the key for reaction updates)
            reactions: editedMessage.reactions || oldMessage.reactions || [],
            // Update read/delivery status from server
            readBy: editedMessage.readBy || oldMessage.readBy,
            deliveredTo: editedMessage.deliveredTo || oldMessage.deliveredTo,
            // Update other server properties
            replyTo: editedMessage.replyTo?._id || editedMessage.replyTo || oldMessage.replyTo,
            replyToText:
              editedMessage.replyTo?.message || editedMessage.replyToText || oldMessage.replyToText,
            replyToSender:
              editedMessage.replyTo?.senderId?.fullName ||
              editedMessage.replyTo?.senderId?.userName ||
              editedMessage.replyToSender ||
              oldMessage.replyToSender,
            replyToAttachments:
              editedMessage.replyTo?.attachments ||
              editedMessage.replyToAttachments ||
              oldMessage.replyToAttachments ||
              [],
            repliedBy: editedMessage.repliedBy || oldMessage.repliedBy,
            // Update buttonDisabled - server sends it at root level (data.buttonDisabled) or inside message
            buttonDisabled: data.buttonDisabled !== undefined 
              ? data.buttonDisabled 
              : (editedMessage.buttonDisabled !== undefined 
                ? editedMessage.buttonDisabled 
                : oldMessage.buttonDisabled),
            isDeletedForMe: editedMessage.isDeletedForMe || oldMessage.isDeletedForMe,
            isDeletedForEveryone: editedMessage.isDeletedForEveryone || oldMessage.isDeletedForEveryone,
          };

          // Replace the message in the array
          const updatedMessages = [...prev];
          updatedMessages[messageIndex] = updatedMessage;
          return updatedMessages;
        }

        // If message not found, return previous state
        return prev;
      });
    };

    // Listener for mini-conversation messages (from messageReceivedMiniConversation)
    const handleMiniConversationMessageReceived = (data) => {
     
      // Handle different message data structures
      let message = null;
      let miniConvId = null;

      // Check if data has a batch property (batch format)
      if (data.batch && Array.isArray(data.batch)) {
        // Handle batch messages
        for (const batchItem of data.batch) {
          if (batchItem.message) {
            const incoming = batchItem.message;
            
            // Extract miniConversationId from message - it can be an object with _id or a string
            const messageMiniConvId = 
              incoming.miniConversationId?._id || 
              incoming.miniConversationId || 
              batchItem.conversationId || // Fallback to batchItem conversationId
              incoming.conversationId; // Fallback to message conversationId
            
          
            // Check if this message belongs to the current mini conversation
            // Also check if conversationType is "miniConversation"
            const isMiniConversationMessage = incoming.conversationType === "miniConversation";
            const matchesMiniConvId = String(messageMiniConvId) === String(miniConversationId);
            
           
            // Convert both to strings for reliable comparison
            if (isMiniConversationMessage && matchesMiniConvId) {
              message = incoming;
              miniConvId = messageMiniConvId;
              break;
            }
          }
        }
      } else if (data.message) {
        // Single message format
        const incoming = data.message;
        const messageMiniConvId = 
          incoming.miniConversationId?._id || 
          incoming.miniConversationId || 
          data.conversationId;
        
        const isMiniConversationMessage = incoming.conversationType === "miniConversation";
        const matchesMiniConvId = String(messageMiniConvId) === String(miniConversationId);
        
        if (isMiniConversationMessage && matchesMiniConvId) {
          message = incoming;
          miniConvId = messageMiniConvId;
        }
      } else {
        // Direct message format
        const messageMiniConvId = 
          data.miniConversationId?._id || 
          data.miniConversationId || 
          data.conversationId;
        
        const isMiniConversationMessage = data.conversationType === "miniConversation";
        const matchesMiniConvId = String(messageMiniConvId) === String(miniConversationId);
        
        if (isMiniConversationMessage && matchesMiniConvId) {
          message = data;
          miniConvId = messageMiniConvId;
        }
      }

      // Only process messages for this mini conversation
      if (message && miniConvId) {
        console.log("Adding message to state:", message);
        setMessages((prev) => {
          // Check if message already exists
          const messageId = message._id || message.id;
          const existingIndex = prev.findIndex(
            (msg) => (msg._id || msg.id) === messageId
          );

          // Calculate isOwn property
          const senderIdStr = String(message.senderId?._id || message.senderId || "");
          const currentUserIdStr = String(currentUserId || "");
          const isOwnMessage = senderIdStr === currentUserIdStr;

          // Mark isOwn property and transform message
          // Add reply information - handle both ID and full object formats
          const processedMessage = {
            ...message,
            id: message._id || message.id,
            isOwn: isOwnMessage,
            text: message.message || message.text || "",
            timestamp: message.createdAt
              ? moment(message.createdAt).format("HH:mm")
              : "",
            attachments: Array.isArray(message.attachments) ? message.attachments : [],
            reactions: message.reactions || [], // Preserve reactions from backend
            // Add reply information - handle both ID and full object formats
            replyTo: message.replyTo?._id || message.replyTo || null,
            replyToText:
              message.replyTo?.message || message.replyToText || null,
            replyToSender:
              message.replyTo?.senderId?.fullName ||
              message.replyTo?.senderId?.userName ||
              message.replyToSender ||
              null,
            // Include attachments from the original replied message if available
            replyToAttachments:
              message.replyTo?.attachments ||
              message.replyToAttachments ||
              [],
          };

          // If message exists, update it (for reaction updates, edits, etc.)
          if (existingIndex !== -1) {
            console.log("Message already exists, updating with new data:", processedMessage);
            const updatedMessages = [...prev];
            updatedMessages[existingIndex] = processedMessage;
            return updatedMessages;
          }

          console.log("Adding new message to messages array:", processedMessage);
          // Add new message
          return [...prev, processedMessage];
        });
      } else {
        console.log("Message not processed - message:", message, "miniConvId:", miniConvId);
      }
    };

    addEventListener("messageReceived", handleMessageReceived);
    addEventListener("messagesReceived", handleMessagesReceived);
    addEventListener("messageReceivedMiniConversation", handleMiniConversationMessageReceived);
    addEventListener("messagesFetchedMiniConversation", handleMessagesFetchedMiniConversation);
    addEventListener("messageEdited", handleMessageEdited);

    // Store cleanup function with unique key per dialog instance
    // Initialize cleanup registry if it doesn't exist
    if (!window._notesActionSheetCleanupRegistry) {
      window._notesActionSheetCleanupRegistry = {};
    }
    
    // Store cleanup function for this specific dialog instance
    window._notesActionSheetCleanupRegistry[miniConversationId] = () => {
      removeEventListener("messageReceived", handleMessageReceived);
      removeEventListener("messagesReceived", handleMessagesReceived);
      removeEventListener("messageReceivedMiniConversation", handleMiniConversationMessageReceived);
      removeEventListener("messagesFetchedMiniConversation", handleMessagesFetchedMiniConversation);
      removeEventListener("messageEdited", handleMessageEdited);
    };
  };

  // Cleanup message listener - only for this specific dialog instance
  const cleanupMessageListener = () => {
    if (window._notesActionSheetCleanupRegistry && window._notesActionSheetCleanupRegistry[miniConversationId]) {
      window._notesActionSheetCleanupRegistry[miniConversationId]();
      delete window._notesActionSheetCleanupRegistry[miniConversationId];
    }
  };

  // Setup mini conversation info listener
  const setupMiniConversationInfoListener = () => {
    if (!miniConversationId) return;

    // Handler for miniConversationInfo event
    const handleMiniConversationInfo = (data) => {
      console.log("handleMiniConversationInfo - received data:", data);
      
      // Validate data
      if (!data || !data.miniConversationInfo) {
        console.log("Invalid mini conversation info data");
        return;
      }

      // Extract miniConversationId from the received data
      const dataMiniConvId = data.miniConversationInfo._id;
      
      // Only process if this info belongs to the current mini conversation
      if (String(dataMiniConvId) !== String(miniConversationId)) {
        console.log("Info is for different conversation, ignoring:", {
          dataMiniConvId,
          currentMiniConvId: miniConversationId
        });
        return;
      }

      // Store the mini conversation info
      setMiniConversationInfo(data.miniConversationInfo);
      console.log("Mini conversation info loaded:", data.miniConversationInfo);
    };

    // Add event listener
    addEventListener("miniConversationInfo", handleMiniConversationInfo);

    // Store cleanup function with unique key per dialog instance
    // Initialize cleanup registry if it doesn't exist
    if (!window._notesActionSheetInfoCleanupRegistry) {
      window._notesActionSheetInfoCleanupRegistry = {};
    }
    
    // Store cleanup function for this specific dialog instance
    window._notesActionSheetInfoCleanupRegistry[miniConversationId] = () => {
      removeEventListener("miniConversationInfo", handleMiniConversationInfo);
    };
  };

  // Cleanup mini conversation info listener - only for this specific dialog instance
  const cleanupMiniConversationInfoListener = () => {
    if (window._notesActionSheetInfoCleanupRegistry && window._notesActionSheetInfoCleanupRegistry[miniConversationId]) {
      window._notesActionSheetInfoCleanupRegistry[miniConversationId]();
      delete window._notesActionSheetInfoCleanupRegistry[miniConversationId];
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (
      (!newMessage.trim() && notesMediaState.selectedFiles.length === 0) ||
      !miniConversationId
    )
      return;

    let attachments = [];
    let messageType = "text";

    // upload media files if any
    if (notesMediaState.selectedFiles.length > 0) {
      messageType = "media";
      attachments = await uploadMediaForNotes(notesMediaState.selectedFiles);

      if (attachments.length === 0) {
        toast.error("failed to upload media. please try again.");
        return;
      }

      // validate attachments structure
      const validAttachments = attachments.filter(
        (att) =>
          att &&
          att.type &&
          att.url &&
          ["image", "video", "audio", "document", "file"].includes(att.type)
      );

      if (validAttachments.length !== attachments.length) {
        toast.error("some media files are invalid. please try again.");
        return;
      }

      attachments = validAttachments;
      // Single attachment → its type; multiple same type → that type; mixed → "media"
      messageType =
        validAttachments.length === 1
          ? validAttachments[0].type
          : validAttachments.every((a) => a.type === validAttachments[0].type)
            ? validAttachments[0].type
            : "media";
    } else if (newMessage.trim()) {
      messageType = "text";
    } else {
      messageType = "media"; // if only media, no text
    }

    try {
      // Generate a temporary message ID
      const messageId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Prepare mini-conversation message payload
      const miniConversationPayload = {
        _id: messageId,
        senderId: currentUserId,
        conversationId: miniConversationId, // This is the main conversation ID
        miniConversationId: miniConversationId, // This is the mini conversation ID
        conversationType: "miniConversation",
        type: messageType,
        message: newMessage.trim() || "",
        attachments: attachments.length > 0 ? attachments : [],
        mentions: messageMentions.map((m) => ({
          userId: m.userId,
          name: m.name,
        })),
        reactions: [],
        replyTo:
          replyPreview.isActive && replyPreview.message
            ? replyPreview.message._id || replyPreview.message.id
            : null, // Add replyTo field
        repliedBy: [],
        forwardedFrom: null,
        forwardedBy: [],
        isDeletedForMe: [],
        isDeletedForEveryone: false,
        isEdited: false,
        editedBy: [],
        deliveredTo: [],
        readBy: [],
        location: null,
        latLong: null,
        orderId: null,
      };

      const success = sendMessageOnMiniConversation(miniConversationPayload);

      if (success) {
        setNewMessage("");
        setMessageMentions([]);
        // Clear reply preview after sending message
        setReplyPreview({
          isActive: false,
          message: null,
        });
      } else {
        toast.error("failed to send message - socket not connected");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("failed to send message");
    }
  };

  // Handle send message with formatted text (for mentions)
  const handleSendMessageWithFormattedText = async (formattedText, mentions) => {
    if (
      (!formattedText.trim() && notesMediaState.selectedFiles.length === 0) ||
      !miniConversationId
    )
      return;

    let attachments = [];
    let messageType = "text";

    // upload media files if any
    if (notesMediaState.selectedFiles.length > 0) {
      messageType = "media";
      attachments = await uploadMediaForNotes(notesMediaState.selectedFiles);

      if (attachments.length === 0) {
        toast.error("failed to upload media. please try again.");
        return;
      }

      // validate attachments structure
      const validAttachments = attachments.filter(
        (att) =>
          att &&
          att.type &&
          att.url &&
          ["image", "video", "audio", "document", "file"].includes(att.type)
      );

      if (validAttachments.length !== attachments.length) {
        toast.error("some media files are invalid. please try again.");
        return;
      }

      attachments = validAttachments;
      // Single attachment → its type; multiple same type → that type; mixed → "media"
      messageType =
        validAttachments.length === 1
          ? validAttachments[0].type
          : validAttachments.every((a) => a.type === validAttachments[0].type)
            ? validAttachments[0].type
            : "media";
    } else if (formattedText.trim()) {
      messageType = "text";
    } else {
      messageType = "media"; // if only media, no text
    }

    try {
      // Generate a temporary message ID
      const messageId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Prepare mini-conversation message payload
      const miniConversationPayload = {
        _id: messageId,
        senderId: currentUserId,
        conversationId: miniConversationId, // This is the main conversation ID
        miniConversationId: miniConversationId, // This is the mini conversation ID
        conversationType: "miniConversation",
        type: messageType,
        message: formattedText.trim() || "",
        attachments: attachments.length > 0 ? attachments : [],
        mentions: (mentions || []).map((m) => ({
          userId: m.userId,
          name: m.name,
        })),
        reactions: [],
        replyTo:
          replyPreview.isActive && replyPreview.message
            ? replyPreview.message._id || replyPreview.message.id
            : null, // Add replyTo field
        repliedBy: [],
        forwardedFrom: null,
        forwardedBy: [],
        isDeletedForMe: [],
        isDeletedForEveryone: false,
        isEdited: false,
        editedBy: [],
        deliveredTo: [],
        readBy: [],
        location: null,
        latLong: null,
        orderId: null,
      };

      const success = sendMessageOnMiniConversation(miniConversationPayload);

      if (success) {
        setNewMessage("");
        setMessageMentions([]);
        // Clear reply preview after sending message
        setReplyPreview({
          isActive: false,
          message: null,
        });
      } else {
        toast.error("failed to send message - socket not connected");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("failed to send message");
    }
  };

  // Handle file select for notes
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      const maxSize = 50 * 1024 * 1024; // 50MB limit
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 50MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setNotesMediaState((prev) => ({
        ...prev,
        selectedFiles: [...prev.selectedFiles, ...validFiles],
      }));
      toast.success(`${validFiles.length} file(s) selected`);
    }

    // Reset the input so the same file can be selected again
    event.target.value = "";
  };

  // Handle remove file
  const handleRemoveFile = (index) => {
    setNotesMediaState((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== index),
    }));
  };

  // Handle clear all files
  const handleClearAllFiles = () => {
    setNotesMediaState((prev) => ({
      ...prev,
      selectedFiles: [],
    }));
  };

  // Get file type helper
  const getFileType = (file) => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("audio/")) return "audio";
    if (
      file.type.includes("pdf") ||
      file.type.includes("document") ||
      file.type.includes("text")
    )
      return "document";
    return "file";
  };

  // Upload media for notes using presigned URLs
  const uploadMediaForNotes = async (files, onProgress) => {
    if (files.length === 0) return [];

    setNotesMediaState((prev) => ({
      ...prev,
      isUploading: true,
      uploadProgress: {},
    }));

    try {
      toast.loading("uploading media files...", { id: "notesMediaUpload" });

      // Use presigned URL upload via upload context
      const uploadResults = await uploadFiles(files, {
        miniConversationId,
      });

      // Transform upload results to match message model structure
      const attachments = uploadResults.map((result) => {
        // Determine file type from category or file type
        let fileType = "document";
        if (result.fileType) {
          if (result.fileType.startsWith("image/")) fileType = "image";
          else if (result.fileType.startsWith("video/")) fileType = "video";
          else if (result.fileType.startsWith("audio/")) fileType = "audio";
          else fileType = "document";
        } else if (result.type) {
          fileType = result.type;
        }

        return {
          type: fileType,
          url: result.fileUrl || result.fileKey,
          fileName: result.fileName || null,
          size: result.fileSize || null,
          thumbnail: null,
          duration: null,
        };
      });

      toast.success("media uploaded successfully", { id: "notesMediaUpload" });
      setNotesMediaState((prev) => ({
        ...prev,
        isUploading: false,
        selectedFiles: [],
        uploadProgress: {},
      }));

      return attachments;
    } catch (error) {
      console.error("Error uploading media:", error);
      toast.error(
        error?.message || "failed to upload media. please try again.",
        {
          id: "notesMediaUpload",
        }
      );
      setNotesMediaState((prev) => ({
        ...prev,
        isUploading: false,
        uploadProgress: {},
      }));
      return [];
    }
  };

  // Handle media click
  const handleMediaClick = () => {
    // TODO: Implement media viewer for notes
  };

  // Handle emoji reaction for mini conversation messages
  const handleEmojiReaction = (messageId, emoji, side) => {
    // Emit socket event for emoji reaction on mini conversation
    // Backend expects: { messageId, emoji }
    if (isConnected && miniConversationId) {
      emit("addReaction", {
        messageId,
        emoji,
      });
    } else {
      toast.error("unable to add reaction - socket not connected");
    }
  };


  // Handle reply to message - sets the reply preview state
  const handleReplyToMessage = (message) => {
    setReplyPreview({
      isActive: true,
      message: message,
    });
  };

  // Handle cancel reply - clears the reply preview state
  const handleCancelReply = () => {
    setReplyPreview({
      isActive: false,
      message: null,
    });
  };

  // Handle view order
  const handleViewOrder = () => {
    // TODO: Implement view order for notes
  };

  // Handle open payment dialog
  const handleOpenPaymentDialog = () => {
    // TODO: Implement payment dialog for notes
  };

  // Handle payment response
  const handlePaymentResponse = () => {
    // TODO: Implement payment response for notes
  };

  // Handle search menu item by name
  const handleSearchMenuItemByName = () => {
    // TODO: Implement menu item search for notes
  };

  if (!open) return null;

  // Extract note title from mini conversation info or message text
  // Display item name with notes if available from miniConversationInfo
  const getNoteTitle = () => {
    // First, try to get the item name from miniConversationInfo.notes
    if (miniConversationInfo?.notes) {
      // Check if notes is an object with itemName or item name property
      const itemName = miniConversationInfo.notes.itemName || 
                       miniConversationInfo.notes.item?.name ||
                       miniConversationInfo.notes.name ||
                       (typeof miniConversationInfo.notes === 'string' ? miniConversationInfo.notes : null);
      
      if (itemName) {
        return itemName;
      }
    }
    
    // Fallback: try to extract from message text (e.g., "response on your note on \"orange juice\":")
    if (message?.message || message?.text) {
      const text = message.message || message.text;
      const match = text.match(/note on "([^"]+)"/);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    // Final fallback: use first 8 characters of miniConversationId
    return miniConversationId?.substring(0, 8) || "note";
  };

  return (
    <CustomOverlay>
      <CustomDialog
        ref={dialogRef}
        style={{ left: dialogPosition.x, top: dialogPosition.y }}
      >
        {/* Header with drag handle */}
        <DialogHeader onMouseDown={handleDragStart}>
          <DragHandle />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.125rem" },
                textTransform: "lowercase",
                ml: { xs: 1, sm: 2 },
                color: "#1f2937",
              }}
            >
              {miniConversationInfo?.notes ? `notes: ${getNoteTitle()} ` : `notes - ${getNoteTitle()}`}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#7C3AED",
              backgroundColor: "#f0f4ff",
              "&:hover": {
                backgroundColor: "#7C3AED",
                color: "white",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogHeader>

        {/* Content Container */}
        <ContentContainer>
          {/* Messages Container */}
          <MessagesContainer ref={messagesContainerRef}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: theme.spacing(4),
                }}
              >
                <CircularProgress size={24} sx={{ color: "#7C3AED" }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ ml: 2, textTransform: "lowercase" }}
                >
                  loading messages...
                </Typography>
              </Box>
            ) : messages.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: theme.spacing(4),
                }}
              >
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ textTransform: "lowercase" }}
                >
                  no messages yet
                </Typography>
              </Box>
            ) : (
              messages.map((msg) => {
                // Ensure isOwn is correctly calculated
                // Use the isOwn property from the message if it exists, otherwise calculate it
                const messageIsOwn = msg.isOwn !== undefined 
                  ? msg.isOwn 
                  : String(msg.senderId?._id || msg.senderId || "") === String(currentUserId || "");
                
                return (
                  <MessageBubble
                    key={msg._id || msg.id}
                    message={msg}
                    isOwn={messageIsOwn}
                    onMediaClick={handleMediaClick}
                    onEmojiReaction={handleEmojiReaction}
                    onReplyToMessage={handleReplyToMessage}
                    onViewOrder={handleViewOrder}
                    onOpenPaymentDialog={handleOpenPaymentDialog}
                    onPaymentResponse={handlePaymentResponse}
                    onSearchMenuItemByName={handleSearchMenuItemByName}
                    selectedContact={null}
                    conversationInfo={null}
                    isCurrentUserOwnerOrTeamMember={false}
                    enableInAppWalletPayments={true}
                    businessLocationName=""
                  />
                );
              })
            )}
          </MessagesContainer>

          {/* Message Input - Separate instance for notes with unique file input ID */}
          <Box sx={{ borderTop: "1px solid #e5e7eb", backgroundColor: "white" }}>
            <MessageInput
              newMessage={newMessage}
              onInputChange={handleInputChange}
              onSendMessage={handleSendMessage}
              onSendMessageWithFormattedText={handleSendMessageWithFormattedText}
              onFileSelect={handleFileSelect}
              onOpenChatCategories={() => {}}
              onKeyPress={handleKeyPress}
              isConnected={isConnected}
              isUploading={notesMediaState.isUploading}
              selectedFiles={notesMediaState.selectedFiles}
              onRemoveFile={handleRemoveFile}
              onClearAllFiles={handleClearAllFiles}
              uploadProgress={notesMediaState.uploadProgress}
              conversationInfo={null}
              isSidebarOpen={false}
              replyPreview={replyPreview}
              onCancelReply={handleCancelReply}
              onMentionsChange={setMessageMentions}
              isAiStreaming={false}
              fileInputId={`notes-file-input-${miniConversationId}`} // Unique ID per mini conversation
            />
          </Box>
        </ContentContainer>

        {/* Resize Handle */}
        <ResizeHandle onMouseDown={handleResizeStart} />
      </CustomDialog>
    </CustomOverlay>
  );
};

export default NotesActionSheet;

